import { authStore, refreshSession } from '@/shared/auth/session'

import { ApiError, type Problem } from './problem'

export { ApiError, errorMessage, type Problem } from './problem'

export const API_BASE = '/api/v1'

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  json?: unknown
  /** Multipart body (file uploads); the browser sets the boundary header. */
  form?: FormData
  /** Treat these non-2xx statuses as data instead of throwing (e.g. 503 readiness). */
  acceptStatus?: number[]
  /** Send the access token (default true). */
  auth?: boolean
  /** Send the active workspace header (default true when authenticated). */
  workspace?: boolean
}

async function send(path: string, opts: RequestOptions): Promise<Response> {
  const { json, form, headers: extra, auth = true, workspace = true, acceptStatus: _a, ...init } = opts
  const headers = new Headers(extra)
  if (!headers.has('Accept')) headers.set('Accept', 'application/json')
  if (json !== undefined) headers.set('Content-Type', 'application/json')
  const { accessToken, workspaceId } = authStore.get()
  if (auth && accessToken) headers.set('Authorization', `Bearer ${accessToken}`)
  if (auth && workspace && workspaceId) headers.set('X-Provyn-Workspace', workspaceId)
  return fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: 'same-origin',
    headers,
    ...(json !== undefined ? { body: JSON.stringify(json) } : form ? { body: form } : {}),
  })
}

export async function apiFetch<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  let res = await send(path, opts)
  // Access token expired (or tab woke from sleep): refresh once and retry.
  if (res.status === 401 && opts.auth !== false && authStore.get().accessToken) {
    if (await refreshSession()) res = await send(path, opts)
  }
  if (res.status === 204) return undefined as T
  const isJson = (res.headers.get('content-type') ?? '').includes('json')
  const body: unknown = isJson ? await res.json() : await res.text()
  if (!res.ok && !(opts.acceptStatus ?? []).includes(res.status)) {
    const problem = isJson && body && typeof body === 'object' && 'status' in body ? (body as Problem) : null
    throw new ApiError(res.status, problem, body)
  }
  return body as T
}

/** Convenience wrappers. */
export const api = {
  get: <T>(path: string, opts?: RequestOptions) => apiFetch<T>(path, { ...opts, method: 'GET' }),
  post: <T>(path: string, json?: unknown, opts?: RequestOptions) =>
    apiFetch<T>(path, { ...opts, method: 'POST', json }),
  patch: <T>(path: string, json?: unknown, opts?: RequestOptions) =>
    apiFetch<T>(path, { ...opts, method: 'PATCH', json }),
  put: <T>(path: string, json?: unknown, opts?: RequestOptions) =>
    apiFetch<T>(path, { ...opts, method: 'PUT', json }),
  upload: <T>(path: string, form: FormData, opts?: RequestOptions) =>
    apiFetch<T>(path, { ...opts, method: 'POST', form }),
  delete: <T = void>(path: string, opts?: RequestOptions) => apiFetch<T>(path, { ...opts, method: 'DELETE' }),
}
